import { createTheme } from '@mui/material/styles';

const primaryAccent = '#08D2FF';
const highlightAccent = '#8F9BFF';
const backgroundDeep = '#0C111C';
const backgroundSurface = '#141B2B';
const backgroundElevated = '#1A2235';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: primaryAccent,
      light: '#41D7FF',
      dark: '#0190B5',
      contrastText: '#0A0D14',
    },
    secondary: {
      main: '#7B7DF8',
      light: '#9A9CFB',
      dark: '#4F51CB',
      contrastText: '#0A0D14',
    },
    background: {
      default: backgroundDeep,
      paper: backgroundSurface,
    },
    text: {
      primary: '#F5F8FF',
      secondary: 'rgba(245, 248, 255, 0.82)',
      disabled: 'rgba(245, 248, 255, 0.38)',
    },
    divider: 'rgba(245, 248, 255, 0.08)',
    success: {
      main: '#3DD598',
    },
    warning: {
      main: '#FFC542',
    },
    info: {
      main: '#58C0F2',
    },
    error: {
      main: '#FF6B6B',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '3.2rem',
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.4rem',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.9rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '-0.01em',
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: '1.08rem',
      color: 'rgba(245, 248, 255, 0.82)',
    },
    body1: {
      fontSize: '0.98rem',
      lineHeight: 1.7,
    },
    body2: {
      fontSize: '0.92rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 6,
  },
  spacing: 8,
  shadows: [
    'none',
    '0px 14px 80px rgba(3, 198, 252, 0.08)',
    '0px 12px 40px rgba(0, 0, 0, 0.35)',
    ...Array(22).fill('0px 14px 80px rgba(0,0,0,0.35)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: backgroundDeep,
          color: '#F5F8FF',
          minHeight: '100vh',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: backgroundSurface,
          borderRadius: 6,
          border: '1px solid rgba(245, 248, 255, 0.06)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: backgroundElevated,
          borderRadius: 6,
          padding: '26px',
          border: '1px solid rgba(245, 248, 255, 0.08)',
          boxShadow: '0 24px 65px rgba(8, 210, 255, 0.12)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 28px 80px rgba(8, 210, 255, 0.18)',
            borderColor: 'rgba(143, 155, 255, 0.45)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          borderRadius: 6,
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(245, 248, 255, 0.14)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(8, 210, 255, 0.65)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryAccent,
            boxShadow: '0 0 0 3px rgba(8, 210, 255, 0.18)',
          },
        },
        input: {
          color: '0 0 0 3px rgba(8, 210, 255, 0.18)',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          padding: '11px 22px',
        },
        containedPrimary: {
          color: '#0A0D14',
          background:
            'linear-gradient(135deg, rgba(8,210,255,0.95) 0%, rgba(143,155,255,0.95) 60%)',
          boxShadow: '0 12px 30px rgba(8,210,255,0.35)',
          '&:hover': {
            background:
              'linear-gradient(135deg, rgba(8,210,255,1) 0%, rgba(143,155,255,1) 60%)',
            boxShadow: '0 16px 38px rgba(8,210,255,0.45)',
          },
        },
        outlined: {
          borderColor: 'rgba(245, 248, 255, 0.25)',
          '&:hover': {
            borderColor: primaryAccent,
            backgroundColor: 'rgba(8, 210, 255, 0.15)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(245, 248, 255, 0.08)',
        },
        head: {
          color: 'rgba(30, 31, 31, 0.82)',
          fontWeight: 600,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          color: '#F5F8FF',
          fontWeight: 500,
        },
        outlined: {
          borderColor: 'rgba(245, 248, 255, 0.22)',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&.Mui-selected': {
            backgroundColor: 'rgba(3, 198, 252, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(3, 198, 252, 0.16)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(245, 248, 255, 0.05)',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.06)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1C212C',
          color: '#F2F4F8',
        },
      },
    },
  },
});

export default theme;
